import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Breadcrumb from '../../components/feature/Breadcrumb';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import AnimatedStatCard from '@/components/feature/AnimatedStatCard';

const timeline = [
  { year: '2007', title: 'Fundação', description: 'Início das operações em São Paulo com foco em eventos corporativos inovadores.' },
  { year: '2011', title: 'WorldSkills London', description: 'Atuação da STIB na WorldSkills London 2011, no Reino Unido — a maior competição mundial de educação profissional.' },
  { year: '2013', title: 'WorldSkills Leipzig', description: 'Segunda edição consecutiva do WorldSkills atendida pela equipe, em Leipzig, na Alemanha.' },
  { year: '2015', title: 'WorldSkills São Paulo', description: 'A competição chega ao Brasil e a STIB atende a WorldSkills São Paulo 2015.' },
  { year: '2017', title: 'WorldSkills Abu Dhabi', description: 'Quarta edição consecutiva: WorldSkills Abu Dhabi 2017, nos Emirados Árabes Unidos.' },
  { year: '2020', title: 'Tecnologia', description: 'Implementação de sistema com 5 canais simultâneos e tradução em tempo real.' },
];

const values = [
  { icon: 'ri-lightbulb-line', title: 'Inovação', description: 'Buscamos constantemente as tecnologias mais avançadas para oferecer experiências únicas.' },
  { icon: 'ri-star-line', title: 'Excelência', description: 'Compromisso com a qualidade em cada detalhe, do planejamento à execução.' },
  { icon: 'ri-customer-service-2-line', title: 'Suporte', description: 'Equipe dedicada disponível 24/7 para garantir o sucesso do seu evento.' },
  { icon: 'ri-shield-check-line', title: 'Confiabilidade', description: 'Equipamentos de última geração e processos rigorosos de qualidade.' },
];

export default function AboutPage() {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');
    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => formBody.append(key, value));
      const response = await fetch('https://ordem-de-servico.stibpalestrasilenciosasp.com.br/pedido.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });
      if (response.ok) {
        setSubmitMessage('Mensagem enviada com sucesso! Entraremos em contato em breve.');
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      } else {
        setSubmitMessage('Erro ao enviar. Por favor, tente novamente.');
      }
    } catch {
      setSubmitMessage('Erro ao enviar. Por favor, tente novamente.');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitMessage(''), 8000);
    }
  };

  useEffect(() => {
    document.title = 'Quem Somos | STIB Palestra Silenciosa São Paulo';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) metaDescription.setAttribute('content', 'Conheça a STIB. Desde 2007, pioneiros em tecnologia de áudio sem fio para eventos corporativos. +500 eventos, 150.000 participantes.');

    const lastModified = document.querySelector('meta[name="last-modified"]');
    if (lastModified) lastModified.setAttribute('content', '2026-03-24');

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://stibpalestrasilenciosasp.com.br';

    // AboutPage schema
    const aboutSchema = {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: 'Quem Somos - STIB Palestra Silenciosa',
      url: `${siteUrl}/quem-somos`,
      description: 'Conheça a STIB. Pioneiros em tecnologia de áudio sem fio para eventos corporativos desde 2007.',
      mainEntity: {
        '@type': 'Organization',
        name: 'STIB - Palestra Silenciosa São Paulo',
        foundingDate: '2007',
        foundingLocation: {
          '@type': 'Place',
          name: 'São Paulo, SP, Brasil'
        },
        numberOfEmployees: { '@type': 'QuantitativeValue', value: '11+' },
        description: 'Especialista em tecnologia de áudio sem fio para eventos corporativos, com sede em São Paulo. +500 eventos, 150.000 participantes atendidos.',
        url: siteUrl,
        telephone: '+5511917776227',
        email: 'contato@stibpalestrasilenciosasp.com.br',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'São Paulo',
          addressRegion: 'SP',
          addressCountry: 'BR'
        },
        sameAs: [siteUrl]
      }
    };

    // Person schema for Daniel BC Fernandes
    const personSchema = {
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: 'Daniel BC Fernandes',
      jobTitle: 'Fundador & CEO',
      worksFor: {
        '@type': 'Organization',
        name: 'STIB - Palestra Silenciosa São Paulo',
        url: siteUrl
      },
      knowsAbout: ['Tecnologia de Áudio Sem Fio', 'Eventos Corporativos', 'Palestra Silenciosa', 'Silent Conference', 'Silent Disco', 'Tradução Simultânea'],
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'São Paulo',
        addressRegion: 'SP',
        addressCountry: 'BR'
      },
      url: `${siteUrl}/quem-somos`,
      description: 'Especialista em tecnologia de áudio sem fio para eventos com +18 anos de experiência no mercado corporativo brasileiro.'
    };

    // BreadcrumbList schema
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Quem Somos', item: `${siteUrl}/quem-somos` }
      ]
    };

    const ids = ['about-schema', 'about-person-schema', 'about-breadcrumb-schema'];
    ids.forEach(id => { document.getElementById(id)?.remove(); });

    const s1 = document.createElement('script');
    s1.type = 'application/ld+json';
    s1.id = 'about-schema';
    s1.text = JSON.stringify(aboutSchema);
    document.head.appendChild(s1);

    void personSchema;

    const s3 = document.createElement('script');
    s3.type = 'application/ld+json';
    s3.id = 'about-breadcrumb-schema';
    s3.text = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(s3);

    return () => {
      ids.forEach(id => { document.getElementById(id)?.remove(); });
    };
  }, []);

  const { ref: heroRef, isVisible: heroVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: historyRef, isVisible: historyVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: missionRef, isVisible: missionVisible } = useScrollAnimation({ threshold: 0.15 });
  const { ref: valuesRef, isVisible: valuesVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: statsRef, isVisible: statsVisible } = useScrollAnimation({ threshold: 0.2 });
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollAnimation({ threshold: 0.2 });

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <Header />
      <main>

        {/* Hero */}
        <section className="relative h-[460px] flex items-center justify-center overflow-hidden mt-20">
          <div className="absolute inset-0">
            <img
              src="/img/stib/escritorio-moderno.webp"
              alt="Equipe STIB - Especialistas em tecnologia de áudio para eventos"
              className="w-full h-full object-cover object-top animate-hero-zoom"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-secondary/75 via-secondary/55 to-secondary/75"></div>
          </div>

          <div className="absolute top-6 left-0 right-0 z-20">
            <div className="max-w-7xl mx-auto px-6">
              <Breadcrumb variant="light" items={[{ label: 'Home', to: '/' }, { label: 'Quem Somos' }]} />
            </div>
          </div>

          <div ref={heroRef} className="relative z-10 text-center px-6 w-full">
            <span className={`font-body font-semibold text-sm uppercase tracking-widest mb-4 block transition-all duration-700 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{ color: '#18c7f4' }}>
              Nossa Empresa
            </span>
            <h1 className={`font-heading font-bold text-6xl md:text-7xl text-white mb-5 transition-all duration-700 delay-100 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              QUEM SOMOS
            </h1>
            <p className={`font-body text-xl text-white/85 max-w-2xl mx-auto transition-all duration-700 delay-200 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              Pioneiros em <strong>tecnologia de áudio sem fio</strong> para <strong>eventos corporativos</strong> em todo o Brasil
            </p>
          </div>
        </section>



        {/* Nossa História */}
        <section className="py-24 bg-white dark:bg-gray-900">
          <div className="max-w-7xl mx-auto px-6">
            <div ref={historyRef} className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
              <div className={`transition-all duration-700 ${historyVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
                <span className="font-body font-semibold text-sm uppercase tracking-widest mb-4 block text-primary">
                  Nossa Trajetória
                </span>
                <h2 className="font-heading font-bold text-5xl text-secondary dark:text-white mb-8">
                  Uma Década de Inovação
                </h2>
                <div className="space-y-5">
                  <p className="font-body text-base text-gray-600 dark:text-gray-300 leading-relaxed">
                    Fundada em 2007, a STIB nasceu para revolucionar a forma como eventos corporativos são realizados. Identificamos que a poluição sonora e as limitações de espaço eram os maiores desafios dos organizadores.
                  </p>
                  <p className="font-body text-base text-gray-600 dark:text-gray-300 leading-relaxed">
                    Com investimento em tecnologia de ponta, desenvolvemos soluções que permitem múltiplas sessões simultâneas, tradução em tempo real e experiências personalizadas para cada participante.
                  </p>
                  <p className="font-body text-base text-gray-600 dark:text-gray-300 leading-relaxed">
                    Hoje somos referência no mercado, tendo atendido mais de <strong>500 eventos</strong> e <strong>150.000 participantes</strong>, incluindo as maiores empresas e instituições do país.
                  </p>
                </div>
              </div>
              <div className={`grid grid-cols-3 gap-4 transition-all duration-700 delay-200 ${historyVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
                {timeline.map((item, i) => (
                  <div
                    key={item.year}
                    className={`relative bg-gray-50 dark:bg-gray-800 rounded-2xl p-5 hover:bg-primary/5 dark:hover:bg-gray-700 transition-all duration-300 hover:-translate-y-1 ${historyVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                    style={{ transitionDelay: `${200 + i * 80}ms` }}
                  >
                    <div className="absolute -top-3 left-4 px-3 py-1 rounded-full font-heading font-bold text-xs text-white" style={{ background: '#18c7f4', color: '#384774' }}>
                      {item.year}
                    </div>
                    <h4 className="font-heading font-bold text-base text-secondary dark:text-white mt-3 mb-2">{item.title}</h4>
                    <p className="font-body text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Missão e Visão */}
        <section className="py-20 bg-gray-50 dark:bg-gray-800">
          <div className="max-w-7xl mx-auto px-6">
            <div ref={missionRef} className="grid grid-cols-1 md:grid-cols-2 gap-10">
              <div className={`rounded-3xl p-12 transition-all duration-700 ${missionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ background: 'linear-gradient(135deg, rgba(24,199,244,0.08), rgba(56,71,116,0.08))' }}>
                <div className={`w-14 h-14 flex items-center justify-center rounded-full mb-6 transition-all duration-700 delay-200 ${missionVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`} style={{ background: '#18c7f4' }}>
                  <i className="ri-target-line text-white text-2xl"></i>
                </div>
                <h2 className="font-heading font-bold text-3xl text-secondary dark:text-white mb-5">Nossa Missão</h2>
                <p className="font-body text-base text-gray-600 dark:text-gray-300 leading-relaxed">
                  Proporcionar experiências únicas em eventos corporativos através de tecnologia de áudio inovadora, garantindo comunicação clara e eficiente sem interferências externas.
                </p>
              </div>
              <div className={`rounded-3xl p-12 transition-all duration-700 delay-200 ${missionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ background: 'linear-gradient(135deg, rgba(56,71,116,0.08), rgba(24,199,244,0.08))' }}>
                <div className={`w-14 h-14 flex items-center justify-center rounded-full bg-secondary mb-6 transition-all duration-700 delay-400 ${missionVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`}>
                  <i className="ri-eye-line text-white text-2xl"></i>
                </div>
                <h2 className="font-heading font-bold text-3xl text-secondary dark:text-white mb-5">Nossa Visão</h2>
                <p className="font-body text-base text-gray-600 dark:text-gray-300 leading-relaxed">
                  Ser referência nacional em soluções de áudio para eventos corporativos, reconhecida pela excelência técnica, inovação constante e pelo impacto positivo nas comunicações empresariais.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Nossos Valores */}
        <section className="py-24 bg-white dark:bg-gray-900">
          <div className="max-w-7xl mx-auto px-6">
            <div ref={valuesRef} className="text-center mb-14">
              <span className={`font-body text-primary font-semibold text-sm uppercase tracking-widest mb-3 block transition-all duration-700 ${valuesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>O que nos guia</span>
              <h2 className={`font-heading font-bold text-5xl text-secondary dark:text-white transition-all duration-700 delay-100 ${valuesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>Nossos Valores</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <div
                  key={index}
                  className={`bg-gray-50 dark:bg-gray-800 rounded-2xl p-8 text-center border-2 border-transparent hover:border-primary/30 transition-all duration-500 hover:-translate-y-1 ${valuesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <div className="w-14 h-14 flex items-center justify-center rounded-full mx-auto mb-5 transition-transform duration-500 hover:scale-110" style={{ background: 'linear-gradient(135deg, #18c7f4, #384774)' }}>
                    <i className={`${value.icon} text-white text-2xl`}></i>
                  </div>
                  <h3 className="font-heading font-bold text-lg text-secondary dark:text-white mb-3">{value.title}</h3>
                  <p className="font-body text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-24 bg-secondary dark:bg-gray-800">
          <div ref={statsRef} className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-12 text-center">
              {[
                { numericValue: 500, suffix: '+', label: 'Eventos Realizados' },
                { numericValue: 150, suffix: 'K+', label: 'Participantes Atendidos' },
                { numericValue: 98, suffix: '%', label: 'Satisfação dos Clientes' },
                { numericValue: 11, suffix: '', label: 'Anos de Experiência' },
              ].map((stat, i) => (
                <AnimatedStatCard
                  key={stat.label}
                  numericValue={stat.numericValue}
                  suffix={stat.suffix}
                  label={stat.label}
                  delay={i * 120}
                  visible={statsVisible}
                  numberColor="text-primary"
                  className="text-center"
                />
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-24 bg-white dark:bg-gray-900">
          <div ref={ctaRef} className="max-w-4xl mx-auto px-6 text-center">
            <span className={`font-body text-primary font-semibold text-sm uppercase tracking-widest mb-4 block transition-all duration-700 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>Vamos conversar</span>
            <h2 className={`font-heading font-bold text-5xl text-secondary dark:text-white mb-6 transition-all duration-700 delay-100 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              Pronto para Transformar Seu Evento?
            </h2>
            <p className={`font-body text-lg text-gray-600 dark:text-gray-300 mb-10 max-w-xl mx-auto transition-all duration-700 delay-200 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              Fale diretamente com nossa equipe e descubra a solução certa para o seu evento.
            </p>
            <div className={`flex flex-col sm:flex-row gap-4 justify-center transition-all duration-700 delay-300 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <Link
                to="/contato"
                className="inline-flex items-center gap-3 px-10 py-4 rounded-full font-body font-bold text-sm uppercase transition-all hover:scale-105 whitespace-nowrap cursor-pointer text-white hover:opacity-90"
                style={{ background: '#384774' }}
              >
                <i className="ri-mail-send-line text-lg"></i>
                Solicitar Orçamento
              </Link>
              <a
                href="https://wa.me/5511917776227?text=Olá! Gostaria de saber mais sobre os serviços da STIB."
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 px-10 py-4 rounded-full font-body font-bold text-sm uppercase transition-all hover:scale-105 whitespace-nowrap cursor-pointer border-2 text-secondary hover:bg-primary/5"
                style={{ borderColor: '#18c7f4', color: '#384774' }}
              >
                <i className="ri-whatsapp-line text-lg text-green-500"></i>
                WhatsApp
              </a>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
}
